/**
 * Helpers for member tags stored per linked item on a running timer.
 *
 * Each linked module may carry `taggedUserIds`. The "tag on all items" field
 * is derived (the intersection of every module's list) so there is a single
 * source of truth and union semantics fall out naturally: adding through the
 * shared field adds to every item, removing strips from every item, and
 * per-item picks only ever add extras on top.
 */

// Loose on purpose: client-side inferred module types can carry these as
// optional, and the helpers only ever read them.
interface TaggableLinkedModule {
  moduleType?: string | null
  moduleId?: string | null
  taggedUserIds?: string[]
}

export function getLinkedModuleKey(module: {
  moduleType?: string | null
  moduleId?: string | null
}): string {
  return `${module.moduleType ?? ''}:${module.moduleId ?? ''}`
}

const uniqueIds = (ids: Iterable<string>): string[] => [...new Set(ids)]

/** Ids tagged on every module. Empty when any module has no tags. */
export function getSharedTaggedUserIds<T extends TaggableLinkedModule>(
  modules: T[],
): string[] {
  if (modules.length === 0) return []
  const [first, ...rest] = modules
  const firstIds = uniqueIds(first.taggedUserIds ?? [])
  return firstIds.filter(id =>
    rest.every(module => (module.taggedUserIds ?? []).includes(id)),
  )
}

/**
 * Applies an edit of the shared list: ids newly present are added to every
 * module, ids no longer present are removed from every module.
 */
export function applySharedTaggedUserIds<T extends TaggableLinkedModule>(
  modules: T[],
  nextShared: string[],
): T[] {
  const current = new Set(getSharedTaggedUserIds(modules))
  const next = new Set(nextShared)
  const added = [...next].filter(id => !current.has(id))
  const removed = new Set([...current].filter(id => !next.has(id)))

  return modules.map(module => ({
    ...module,
    taggedUserIds: uniqueIds([
      ...(module.taggedUserIds ?? []).filter(id => !removed.has(id)),
      ...added,
    ]),
  }))
}

/** Replaces one module's list, leaving the others untouched. */
export function setModuleTaggedUserIds<T extends TaggableLinkedModule>(
  modules: T[],
  moduleKey: string,
  taggedUserIds: string[],
): T[] {
  return modules.map(module =>
    getLinkedModuleKey(module) === moduleKey
      ? { ...module, taggedUserIds: uniqueIds(taggedUserIds) }
      : module,
  )
}

export function countDistinctTaggedUserIds<T extends TaggableLinkedModule>(
  modules: T[],
): number {
  return uniqueIds(modules.flatMap(module => module.taggedUserIds ?? [])).length
}

/**
 * Pickers rebuild module objects without the tag list. Copy the previous list
 * onto same-key modules that arrive without one, so re-picking never drops
 * tags. Modules that explicitly carry a list keep it.
 */
export function mergeLinkedModuleTags<T extends TaggableLinkedModule>(
  previous: T[],
  next: T[],
): T[] {
  const previousByKey = new Map(
    previous.map(module => [getLinkedModuleKey(module), module]),
  )
  return next.map(module => {
    if (module.taggedUserIds !== undefined) return module
    const prior = previousByKey.get(getLinkedModuleKey(module))
    return prior?.taggedUserIds !== undefined
      ? { ...module, taggedUserIds: prior.taggedUserIds }
      : module
  })
}
